import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

// Geometry helpers. All UVs are in metres; materials set texture repeat so
// texel density is consistent across the ship.

/** Box with UVs in metres on every face. Centred on the origin. */
export function box(w: number, h: number, d: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  const n = g.getAttribute('normal') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i));
    let su: number, sv: number;
    if (ax > 0.5) { su = d; sv = h; }
    else if (ay > 0.5) { su = w; sv = d; }
    else { su = w; sv = h; }
    uv.setXY(i, uv.getX(i) * su, uv.getY(i) * sv);
  }
  return g;
}

/** Surface of revolution about +Y from (radius, y) pairs, UV u around (m), v along profile (m). */
export function lathe(profile: [number, number][], segments = 24): THREE.BufferGeometry {
  const pts = profile.map(([r, y]) => new THREE.Vector2(Math.max(r, 1e-4), y));
  const g = new THREE.LatheGeometry(pts, segments);
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  // Arc length along the profile.
  const len: number[] = [0];
  for (let i = 1; i < pts.length; i++) len.push(len[i - 1]! + pts[i]!.distanceTo(pts[i - 1]!));
  const maxR = Math.max(...profile.map((p) => p[0]));
  const total = len[len.length - 1]! || 1;
  for (let i = 0; i < uv.count; i++) {
    const u = uv.getX(i), v = uv.getY(i);
    const idx = Math.round(v * (pts.length - 1));
    uv.setXY(i, u * Math.PI * 2 * maxR, (len[idx] ?? v * total));
  }
  return g;
}

/** Tapered cylinder along +Y from y0 to y1. */
export function spar(r0: number, r1: number, length: number, radial = 16): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r1, r0, length, radial, Math.max(1, Math.round(length / 2)), false);
  g.translate(0, length / 2, 0);
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  const circ = Math.PI * 2 * Math.max(r0, r1);
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * circ, uv.getY(i) * length);
  return g;
}

/** Orient a geometry built along +Y so it runs from a to b. */
export function alongSegment(g: THREE.BufferGeometry, a: THREE.Vector3, b: THREE.Vector3): THREE.BufferGeometry {
  const dir = new THREE.Vector3().subVectors(b, a);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
  const m = new THREE.Matrix4().compose(a, q, new THREE.Vector3(1, 1, 1));
  return g.applyMatrix4(m);
}

/** Cylinder from a to b with radius r (UVs in metres). */
export function rod(a: THREE.Vector3, b: THREE.Vector3, r0: number, r1 = r0, radial = 10): THREE.BufferGeometry {
  return alongSegment(spar(r0, r1, a.distanceTo(b), radial), a, b);
}

/** Tube through points (Catmull-Rom), UV v in metres. */
export function tube(points: THREE.Vector3[], radius: number, radial = 8, perMetre = 6, closed = false): THREE.BufferGeometry {
  const curve = new THREE.CatmullRomCurve3(points, closed, 'centripetal');
  const len = curve.getLength();
  const seg = Math.max(4, Math.round(len * perMetre));
  const g = new THREE.TubeGeometry(curve, seg, radius, radial, closed);
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getY(i) * Math.PI * 2 * radius, uv.getX(i) * len);
  return g;
}

/**
 * Sweep a closed 2D profile (x = outward/side, y = up) along a path. `side`
 * gives, for each path point, the direction the profile's +x points to.
 */
export function sweep(profile: THREE.Vector2[], path: THREE.Vector3[], side: THREE.Vector3[], closeEnds = true): THREE.BufferGeometry {
  const pos: number[] = [];
  const uvs: number[] = [];
  const idx: number[] = [];
  const n = profile.length;
  const plen: number[] = [0];
  for (let i = 1; i <= n; i++) plen.push(plen[i - 1]! + profile[i % n]!.distanceTo(profile[i - 1]!));
  let along = 0;
  const t = new THREE.Vector3(), up = new THREE.Vector3(), sd = new THREE.Vector3();
  for (let i = 0; i < path.length; i++) {
    if (i > 0) along += path[i]!.distanceTo(path[i - 1]!);
    const a = path[Math.max(0, i - 1)]!, b = path[Math.min(path.length - 1, i + 1)]!;
    t.subVectors(b, a).normalize();
    sd.copy(side[i]!).addScaledVector(t, -side[i]!.dot(t)).normalize();
    up.crossVectors(t, sd).normalize();
    if (up.y < 0) up.negate();
    for (let j = 0; j <= n; j++) {
      const p = profile[j % n]!;
      pos.push(path[i]!.x + sd.x * p.x + up.x * p.y, path[i]!.y + sd.y * p.x + up.y * p.y, path[i]!.z + sd.z * p.x + up.z * p.y);
      uvs.push(along, plen[j]!);
    }
  }
  const row = n + 1;
  for (let i = 0; i < path.length - 1; i++) {
    for (let j = 0; j < n; j++) {
      const a = i * row + j, b = a + 1, c = a + row, d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  // Winding depends on the side/up handedness; fix so normals face outward.
  fixWinding(g, path, side);
  if (!closeEnds) return g;
  return g;
}

function fixWinding(g: THREE.BufferGeometry, path: THREE.Vector3[], side: THREE.Vector3[]): void {
  const n = g.getAttribute('normal') as THREE.BufferAttribute;
  const p = g.getAttribute('position') as THREE.BufferAttribute;
  // Sample: the first vertex's normal should point away from the path point.
  let score = 0;
  const count = Math.min(n.count, 64);
  const c = path[0]!;
  for (let i = 0; i < count; i++) {
    const dx = p.getX(i) - c.x, dy = p.getY(i) - c.y, dz = p.getZ(i) - c.z;
    score += dx * n.getX(i) + dy * n.getY(i) + dz * n.getZ(i);
  }
  void side;
  if (score < 0) {
    const idx = g.getIndex()!;
    for (let i = 0; i < idx.count; i += 3) {
      const b = idx.getX(i + 1);
      idx.setX(i + 1, idx.getX(i + 2));
      idx.setX(i + 2, b);
    }
    g.computeVertexNormals();
  }
}

/** Extrude a 2D polygon (x, y) along z by depth, centred. UVs in metres. */
export function slab(shape: THREE.Vector2[], depth: number, bevel = 0): THREE.BufferGeometry {
  const s = new THREE.Shape(shape);
  const g = new THREE.ExtrudeGeometry(s, {
    depth,
    bevelEnabled: bevel > 0,
    bevelSize: bevel,
    bevelThickness: bevel,
    bevelSegments: 2,
    curveSegments: 8,
  });
  g.translate(0, 0, -depth / 2);
  return g;
}

export function merge(geos: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const prepared = geos.map((g) => {
    const x = g.index ? g.toNonIndexed() : g;
    for (const k of Object.keys(x.attributes)) if (k !== 'position' && k !== 'normal' && k !== 'uv') x.deleteAttribute(k);
    if (!x.getAttribute('uv')) x.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((x.getAttribute('position') as THREE.BufferAttribute).count * 2), 2));
    if (!x.getAttribute('normal')) x.computeVertexNormals();
    return x;
  });
  const m = mergeGeometries(prepared, false);
  if (!m) throw new Error('merge failed');
  return m;
}

/** Accumulates geometry per material key, then emits one mesh per material. */
export class Batch {
  private readonly parts = new Map<string, THREE.BufferGeometry[]>();

  add(key: string, g: THREE.BufferGeometry, m?: THREE.Matrix4): void {
    if (m) g.applyMatrix4(m);
    let list = this.parts.get(key);
    if (!list) this.parts.set(key, (list = []));
    list.push(g);
  }

  build(materials: Record<string, THREE.Material>, castShadow = true, receiveShadow = true): THREE.Group {
    const group = new THREE.Group();
    for (const [key, geos] of this.parts) {
      const mat = materials[key];
      if (!mat) throw new Error(`no material ${key}`);
      const mesh = new THREE.Mesh(merge(geos), mat);
      mesh.castShadow = castShadow;
      mesh.receiveShadow = receiveShadow;
      mesh.name = key;
      group.add(mesh);
    }
    return group;
  }
}

export const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

const templates = new Map<string, THREE.Group>();

/** A mesh group sharing cached geometry, with this ship's own materials. */
export function instantiate(template: THREE.Group, materials: Record<string, THREE.Material>): THREE.Group {
  const g = new THREE.Group();
  for (const c of template.children) {
    const src = c as THREE.Mesh;
    const m = new THREE.Mesh(src.geometry, materials[src.name] ?? src.material);
    m.name = src.name;
    m.castShadow = src.castShadow;
    m.receiveShadow = src.receiveShadow;
    g.add(m);
  }
  return g;
}

/** Build a batch once per key; every ship after the first reuses the geometry. */
export function cachedBatch(key: string, build: (b: Batch) => void, materials: Record<string, THREE.Material>, castShadow = true, receiveShadow = true): THREE.Group {
  let t = templates.get(key);
  if (!t) {
    const b = new Batch();
    build(b);
    t = b.build(materials, castShadow, receiveShadow);
    templates.set(key, t);
  }
  return instantiate(t, materials);
}
